import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { reviewConfigsApi } from '@/lib/api/review-configs.api';

import type {
  ReviewConfig,
  CreateReviewConfigDto,
  UpdateReviewConfigDto,
} from '@repo/shared-types';

export function useReviewConfigs(options?: { enabled?: boolean }) {
  return useQuery<ReviewConfig[]>({
    queryKey: ['review-configs'],
    queryFn: () => reviewConfigsApi.getAll(),
    enabled: options?.enabled ?? true,
  });
}

export function useReviewConfig(id: string | undefined) {
  return useQuery<ReviewConfig>({
    queryKey: ['review-configs', id],
    queryFn: () => reviewConfigsApi.getOne(id!),
    enabled: !!id,
  });
}

export function useCreateReviewConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReviewConfigDto) => reviewConfigsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-configs'] });
    },
  });
}

export function useUpdateReviewConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReviewConfigDto }) =>
      reviewConfigsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-configs'] });
    },
  });
}

export function useDeleteReviewConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => reviewConfigsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-configs'] });
    },
  });
}
